// src/utils/debugProfileMatch.ts
/**
 * Dev-only tracing for the returning-guest profile match and the ID-photo lifecycle.
 *
 * The whole path crosses four stores, two sessionStorage keys, RHF, and a pure resolver, and each
 * hop can independently decide a photo is not usable. Reading the code cannot tell you WHICH hop
 * did it on a given run, because most of the deciding inputs are runtime state that never appears
 * in a network response — a removal decision in sessionStorage, a stale-slot marker, an
 * un-hydrated RHF value. This makes every hop announce itself.
 *
 * # PRIVACY: names and booleans only.
 * Never log a field VALUE, an S3 key, a URL or a document number. `profileMatch.fields` carries
 * the guest's (masked) identity and `args.guestId` elsewhere in this app is a passport number, so
 * the rule here is the same one `redactedErrorForLog` and `removedPhotoSlotsStorage` already
 * follow: slot names, field names, `onFile` booleans, sizes. `summarizeMatch` exists so a caller
 * cannot accidentally spread a raw match into the console.
 *
 * # Where it is on.
 * Gated on BUILD MODE, not on `import.meta.env.DEV` — that is true only under `vite dev`, and the
 * dev environment people actually test against is a BUILD (`build:test`, `--mode testing`, which
 * is the one pointing at api.dev). Gating on DEV meant the deployed dev app logged nothing, which
 * is the opposite of what this file is for.
 *
 *   `vite dev`            MODE=development  -> on
 *   `npm run build:test`  MODE=testing      -> on   (the deployed dev app)
 *   `npm run build`       MODE=production   -> OFF
 *   `npm run build:prod`  MODE=production   -> OFF
 *   vitest                MODE=test         -> off  (keeps the suite readable)
 *
 * `localStorage["gci.debug.pm"]` overrides either way — "1" forces on, "0" forces off — so a
 * production build can be traced deliberately without a redeploy, and a noisy dev session can be
 * silenced without one. Call `window.gciProfileDebug(true|false)` from the console to set it.
 */
import type { ProfileMatch } from "@/types/guest";

const MODE = import.meta.env.MODE;
const ON_BY_BUILD = MODE !== "production" && MODE !== "test";

/** Read once: a localStorage hit per log call would be a real cost at render frequency. */
function readOverride(): boolean | null {
    try {
        const raw = localStorage.getItem("gci.debug.pm");
        if (raw === "1") return true;
        if (raw === "0") return false;
    } catch {
        // Private mode / blocked site data. Fall through to the build default.
    }
    return null;
}

let override = readOverride();
let ENABLED = override ?? ON_BY_BUILD;

if (typeof window !== "undefined") {
    // Console switch, so a tester never needs a rebuild to turn tracing on or off.
    (window as unknown as { gciProfileDebug: (on: boolean) => string }).gciProfileDebug = (on: boolean) => {
        try {
            localStorage.setItem("gci.debug.pm", on ? "1" : "0");
        } catch {
            // Non-fatal: the in-memory flag below still takes effect for this session.
        }
        override = on;
        ENABLED = on;
        return `profile-match tracing ${on ? "ON" : "OFF"} (mode=${MODE})`;
    };
    if (ENABLED) {
        console.log(
            `%c[pm]%c tracing active · mode=${MODE} · window.gciProfileDebug(false) to silence`,
            "color:#7c3aed;font-weight:bold",
            "color:#6b7280",
        );
    }
}

/** Bump to group a whole check-in attempt in the console when several tabs are open. */
const SESSION = Math.random().toString(36).slice(2, 7);

export function dlog(scope: string, event: string, data?: Record<string, unknown>): void {
    if (!ENABLED) return;
    // console.log, deliberately: console.error/warn are asserted on by several suites, and a debug
    // line must never be mistaken for a real failure by a reader or a test.
    if (data === undefined) {
        console.log(`%c[pm:${SESSION}]%c ${scope} · ${event}`, "color:#7c3aed;font-weight:bold", "");
    } else {
        console.log(`%c[pm:${SESSION}]%c ${scope} · ${event}`, "color:#7c3aed;font-weight:bold", "", data);
    }
}

/**
 * A match reduced to what is safe to print: which photos the profile claims, and WHICH fields are
 * on file — never their values.
 */
export function summarizeMatch(match: ProfileMatch | null | undefined): Record<string, unknown> {
    if (match === null) return { match: "null (explicitly cleared)" };
    if (match === undefined) return { match: "undefined (never set)" };
    const photos = match.photos ?? ({} as NonNullable<ProfileMatch["photos"]>);
    const fields = (match.fields ?? {}) as Record<string, { onFile?: boolean } | undefined>;
    return {
        photos: {
            idFront: photos.idFront?.onFile === true,
            idBack: photos.idBack?.onFile === true,
            selfie: photos.selfie?.onFile === true,
        },
        fieldsOnFile: Object.entries(fields)
            .filter(([, v]) => v?.onFile === true)
            .map(([k]) => k),
    };
}

/** A Set of slot names, printed as a sorted array so two logs can be eyeballed against each other. */
export function slotList(set: ReadonlySet<string> | undefined | null): string[] {
    return set ? [...set].sort() : [];
}
